import { useState } from 'react'
import { MessageSquare, X, Send, Bug, Lightbulb, Sparkles, HelpCircle, Check, Loader2 } from 'lucide-react'
import { supabase, isSupabaseConfigured } from '../utils/supabase'

const categories = [
    { id: 'bug', label: 'Bug Report', icon: Bug, color: 'text-red-400' },
    { id: 'feature', label: 'Feature Request', icon: Lightbulb, color: 'text-yellow-400' },
    { id: 'improvement', label: 'Improvement', icon: Sparkles, color: 'text-primary-400' },
    { id: 'other', label: 'Other', icon: HelpCircle, color: 'text-white/60' },
]

export default function FeedbackWidget({ user }) {
    const [isOpen, setIsOpen] = useState(false)
    const [category, setCategory] = useState('')
    const [message, setMessage] = useState('')
    const [status, setStatus] = useState('idle') // idle, loading, success, error
    const [errorMessage, setErrorMessage] = useState('')

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!category) {
            setErrorMessage('Please select a category')
            return
        }
        if (!message.trim()) {
            setErrorMessage('Please enter your feedback')
            return
        }

        setStatus('loading')
        setErrorMessage('')

        try {
            if (isSupabaseConfigured()) {
                // Check spam protection: max 3 unresolved feedbacks per user
                if (user?.id) {
                    const { count, error: countError } = await supabase
                        .from('feedback')
                        .select('*', { count: 'exact', head: true })
                        .eq('user_id', user.id)
                        .not('status', 'in', '("resolved","archived")')

                    if (countError) {
                        console.error('Error checking feedback count:', countError)
                    } else if (count >= 3) {
                        setStatus('error')
                        setErrorMessage('You have reached the limit of 3 pending feedbacks. Please wait for an admin to review your existing feedbacks before submitting more.')
                        return
                    }
                }

                const { error } = await supabase
                    .from('feedback')
                    .insert({
                        user_id: user?.id || null,
                        email: user?.email || null,
                        category,
                        message: message.trim(),
                        page_url: window.location.href,
                        user_agent: navigator.userAgent,
                    })

                if (error) throw error

                // Send email notification to support
                const API_URL = import.meta.env.VITE_API_URL || ''
                fetch(`${API_URL}/api/email/feedback`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        category,
                        message: message.trim(),
                        email: user?.email || null,
                        pageUrl: window.location.href,
                    })
                }).catch(err => console.error('Email notification failed:', err))

            } else {
                // Development fallback
                console.log('Feedback (dev mode):', { category, message })
            }

            setStatus('success')
            setTimeout(() => {
                setIsOpen(false)
                setStatus('idle')
                setCategory('')
                setMessage('')
            }, 2000)

        } catch (error) {
            console.error('Feedback error:', error)
            setStatus('error')
            setErrorMessage('Failed to send feedback. Please try again.')
        }
    }

    return (
        <>
            {/* Floating Button - positioned above VoiceGuide */}
            <button
                onClick={() => setIsOpen(true)}
                className="fixed bottom-20 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center group"
                title="Send Feedback"
            >
                <MessageSquare className="w-6 h-6" />
                <span className="absolute right-full mr-3 px-3 py-1.5 bg-slate-800 text-white text-sm font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    Send Feedback
                </span>
            </button>

            {/* Modal */}
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Modal Content */}
                    <div className="relative w-full max-w-md glass-card p-6 animate-scale-in">
                        {/* Close Button */}
                        <button
                            onClick={() => setIsOpen(false)}
                            className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        {/* Header */}
                        <div className="mb-6">
                            <h3 className="text-xl font-bold text-white mb-1">Send Feedback</h3>
                            <p className="text-white/60 text-sm">
                                Help us improve InkSpot by sharing your thoughts
                            </p>
                        </div>

                        {status === 'success' ? (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                                    <Check className="w-8 h-8 text-green-400" />
                                </div>
                                <h4 className="text-lg font-semibold text-white mb-2">Thank You!</h4>
                                <p className="text-white/60">Your feedback has been received.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                {/* Category Selection */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-white/80 mb-2">
                                        What type of feedback?
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {categories.map((cat) => (
                                            <button
                                                key={cat.id}
                                                type="button"
                                                onClick={() => setCategory(cat.id)}
                                                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-all ${category === cat.id
                                                    ? 'border-primary-500 bg-primary-500/20 text-white'
                                                    : 'border-white/10 bg-white/5 text-white/60 hover:border-white/30 hover:text-white'
                                                    }`}
                                            >
                                                <cat.icon className={`w-4 h-4 ${category === cat.id ? cat.color : ''}`} />
                                                <span className="text-sm">{cat.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Message */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-white/80 mb-2">
                                        Your feedback
                                    </label>
                                    <textarea
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder="Tell us what's on your mind..."
                                        rows={4}
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none resize-none"
                                    />
                                </div>

                                {/* Error Message */}
                                {errorMessage && (
                                    <p className="text-sm text-red-400 mb-4">{errorMessage}</p>
                                )}

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={status === 'loading'}
                                    className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {status === 'loading' ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-5 h-5" />
                                            Send Feedback
                                        </>
                                    )}
                                </button>

                                {/* Privacy Note */}
                                <p className="text-xs text-white/40 text-center mt-4">
                                    {user?.email ? `Sending as ${user.email}` : 'Anonymous feedback'}
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}
